"use client";
import { useAtom } from "jotai";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth, useI18N } from "common/hooks";
import { bucketUrlPrefix } from "common/config";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import { useDarkMode } from "@/hooks/useDarkMode";
import "react-tooltip/dist/react-tooltip.css";
import {
  drawerMenuOpenAtom,
  onboardingCurrentScreenAtom,
  onboardingScreenAtom,
} from "@/store/atoms";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import GetNavigationPages from "./GetNavigationPages";
import { useCallback, useEffect, useRef, useState } from "react";
import ModalWithAction from "@/components/common/ModalWithAction";
import { ArrowSquareOut } from "phosphor-react";
import OnboardingScreensComponent from "../auth/modals/Components/OnboardingScreensComponent";
import AnalyticsService from "@/services/analytics";

function HamburgerMenu() {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const navigate = useNavigate({ from: "/" });
  const { logout, user } = useAuth();
  const profileUrl = bucketUrlPrefix + (user as any)?.profile_photo;
  const { pathname } = window.location;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setIsOnboardingModalOpen] = useAtom(onboardingScreenAtom);
  const [, setOnboardingCurrentScreen] = useAtom(onboardingCurrentScreenAtom);

  // Assuming `user` is defined somewhere in your scope
  const isLoggedIn = !!user?.name; // This checks if user is logged in

  const pages = GetNavigationPages(
    isLoggedIn,
    setIsModalOpen,
    setIsOnboardingModalOpen,
    setOnboardingCurrentScreen
  );

  const [drawerMenuOpen, setDrawerMenuOpen] = useAtom(drawerMenuOpenAtom);
  const [liveMessage, setLiveMessage] = useState("");

  const toggleDrawerMenu = useCallback(() => {
    setDrawerMenuOpen((drawerMenuOpen) => !drawerMenuOpen);
  }, [setDrawerMenuOpen]);

  const handleHamburgerLogout = useCallback(async () => {
    AnalyticsService.trackGAPagesHandler("userLoggedOut");
    AnalyticsService.trackMx("userLoggedOut", {});

    setIsModalOpen(false);
    toggleDrawerMenu();
    logout();
    navigate({ to: "/" });
  }, [logout, navigate, toggleDrawerMenu]);

  const firstFocusHamburgerRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (drawerMenuOpen && firstFocusHamburgerRef.current) {
      console.log("drawerMenuOpen", drawerMenuOpen);
      // Delay focusing to ensure the element is rendered
      setTimeout(() => {

        console.log("drawerMenuOpen focus set");

        firstFocusHamburgerRef.current.focus();
      }, 1000);
    }
  }, [drawerMenuOpen]); // Trigger the effect when the drawer menu opens

  // Handle Escape key press to close the drawer
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && drawerMenuOpen) {
        setDrawerMenuOpen(false);
        setLiveMessage("Menu closed");
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [drawerMenuOpen, setDrawerMenuOpen]);

  return (
    <>
      <div role="none">
        <Drawer
          open={drawerMenuOpen}
          onClose={() => {
            toggleDrawerMenu();
            setLiveMessage("Menu closed");
          }}
          style={{
            width: 320,
            backgroundColor: isDark ? "#11142d" : "#ffffff",
          }}
          direction="right"
          className="p-4 flex flex-col h-screen overflow-y-auto"
          enableOverlay={true}
          aria-label="Navigation drawer"
        >
          <div>
            <div
              onClick={toggleDrawerMenu}
              className="flex items-center gap-3 mb-6"
              role="button"
              tabIndex={1}
              aria-label="Close menu"
            >
              {isDark ? (
                <img
                  src="/images/Close_dark.svg"
                  alt="Close menu"
                  aria-hidden="true"
                />
              ) : (
                <img
                  src="/images/Close.svg"
                  alt="Close menu"
                  aria-hidden="true"
                />
              )}

              <span className="underline font-medium text-black dark:text-white">
                {i18n.get("exitMenu")}
              </span>
            </div>

            {user?.name && (
              <div
                role="button"
                tabIndex={0}
                aria-label={`Profile of ${user?.name}`}
                className="flex items-center gap-4 mb-4"
              >
                {(user as any)?.profile_photo ? (
                  <img
                    className="w-12 h-12 rounded-full"
                    src={profileUrl}
                    alt={`Profile picture of ${user?.name}`}
                  />
                ) : (
                  <NameInitialsAvatar
                    name={user?.name?.split(" ")[0]}
                    bgColor={user?.profile_color_code ? user.profile_color_code : "#8751FF"}
                    textColor="white"
                    textSize="20px"
                    size="48px"
                    borderColor={user?.profile_color_code ? user.profile_color_code : "#8751FF"}
                    aria-label={`Profile initials of ${user?.name}`}
                  />
                )}
                <strong
                  className="desktop-h1-xxsmall-bold"
                  ref={firstFocusHamburgerRef} // Attach the ref here
                >
                  {user?.name}
                </strong>
              </div>
            )}

            {!user?.name && (
              <div
                role="button"
                tabIndex={0}
                aria-label="Welcome, guest profile"
                className="flex items-center gap-4 mb-4"
              >
                <img
                  className="w-12 h-12 rounded-full bg-neutral-300"
                  src="/images/user_placeholder.svg"
                  alt="Guest profile"
                />
                <strong
                  className="desktop-h1-xxsmall-bold"
                  ref={firstFocusHamburgerRef} // Attach the ref here
                >
                  Welcome
                </strong>
              </div>
            )}
          </div>
          <div>
            <div className="flex flex-col gap-10">
              {pages.map(({ elements, group, groupToShowForType }, index) => (
                <div key={index}>
                  {groupToShowForType === "loggedin" && isLoggedIn && (
                    <h4
                      className="text-primary-900 dark:text-white mb-4 desktop-h1-xxsmall-bold"
                      id={`group-${index}`}
                      aria-label={`${group} Links`}
                      tabIndex={0}
                    >
                      {group}
                    </h4>
                  )}

                  {groupToShowForType === "both" && (
                    <h4
                      className="text-primary-900 dark:text-white mb-4 desktop-h1-xxsmall-bold"
                      id={`group-${index}`}
                      aria-label={`${group} Links`}
                      tabIndex={0}
                    >
                      {group}
                    </h4>
                  )}

                  <div className="flex flex-col gap-1" role="none">
                    {elements.map(
                      ({
                        icon,
                        iconDark,
                        name,
                        path,
                        linkToShowForType,
                        onClick,
                        publicUrl,
                      }) => {
                        // Determine if the link should be shown based on user status and linkToShowForType flag
                        const shouldShow = isLoggedIn
                          ? linkToShowForType === "loggedin" ||
                            linkToShowForType === "both"
                          : linkToShowForType === "both";

                        const linkIcon = isDark ? iconDark : icon;
                        const linkTarget = publicUrl ? "_blank" : "_self";
                        const linkPath = publicUrl || path;
                        const publicUrlIcon = publicUrl ? true : false;

                        return (
                          shouldShow && (
                            <Link
                              key={name}
                              className={`flex gap-2 items-center text-blue-950 dark:text-primary-50 rounded p-2 hover:no-underline hover:bg-violet-100 hover:dark:bg-[#37354d] hover:dark:text-primary-150 ${
                                path === pathname
                                  ? "bg-violet-100 dark:bg-[#37354d]"
                                  : ""
                              }`}
                              to={linkPath}
                              target={linkTarget}
                              onClick={onClick}
                              aria-label={`Navigate to ${name}`}
                              role="button"
                            >
                              <img
                                src={linkIcon}
                                alt={`${name} icon`}
                                aria-hidden="true"
                                className="w-[24px] h-[24px] hover:dark:fill-[#DEDDF7]"
                              />
                              <span className="text-primary-800 dark:text-white hover:dark:text-primary-150 paragraph-medium-medium inline-flex items-center">
                                {name}
                                {publicUrlIcon && (
                                  <ArrowSquareOut size={16} className="ml-1" />
                                )}
                              </span>
                            </Link>
                          )
                        );
                      }
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <ModalWithAction
            isModalOpen={isModalOpen}
            type="default"
            actionButtonType="default"
            title={i18n.get("logOut")}
            subtitle={i18n.get("areYouSureYouWishToLogOut")}
            cancelButtonTitle={i18n.get("cancel")}
            actionButtonTitle={i18n.get("logOut")}
            onClose={() => {
              setIsModalOpen(false);
            }}
            onClick={handleHamburgerLogout}
            aria-label="Logout confirmation modal"
          />
        </Drawer>
      </div>
      <div aria-live="assertive" className="sr-only">
        {liveMessage}
      </div>

      <OnboardingScreensComponent onClose={() => {}} />
    </>
  );
}

export default HamburgerMenu;
